import { IAny, IAnyObject } from '@yimoka/shared';
import { Entity as StoreEntity, IStore, IStoreConfig, ISchema } from '@yimoka/store';
import React, { ComponentType, ReactNode, useMemo } from 'react';

import { EntityAuto } from '../entity/auto';

export interface DynamicViewConfig<
  V extends object = IAnyObject,
  R extends object = IAnyObject,
  B extends object = IAnyObject,
> {
  biz?: B;
  components?: Record<string, ComponentType<IAny>>;
  entity?: StoreEntity<V>;
  entityProps?: IAnyObject;
  metadata?: IAnyObject;
  type?: string;
  schema?: ISchema;
  schemaKey?: React.Key;
  schemas?: Record<string, ISchema | undefined>;
  scope?: IAnyObject;
  store?: IStore<V, R> | IStoreConfig<V, R>;
  stores?: Record<string, IStore<V, R> | IStoreConfig<V, R> | undefined>;
}

export interface DynamicViewRenderPayload<
  V extends object = IAnyObject,
  R extends object = IAnyObject,
  B extends object = IAnyObject,
> {
  config: DynamicViewConfig<V, R, B>;
  type?: string;
}

export interface DynamicViewContainerPayload<
  V extends object = IAnyObject,
  R extends object = IAnyObject,
  B extends object = IAnyObject,
> extends DynamicViewRenderPayload<V, R, B> {
  children: ReactNode;
}

export interface DynamicViewProps<
  V extends object = IAnyObject,
  R extends object = IAnyObject,
  B extends object = IAnyObject,
> {
  biz?: B;
  components?: Record<string, ComponentType<IAny>>;
  config?: DynamicViewConfig<V, R, B>;
  entity?: StoreEntity<V>;
  entityProps?: IAnyObject;
  type?: string;
  renderContainer?: (payload: DynamicViewContainerPayload<V, R, B>) => ReactNode;
  renderEntity?: (payload: DynamicViewRenderPayload<V, R, B>) => ReactNode;
  resetKey?: React.Key;
  schema?: ISchema;
  schemaKey?: React.Key;
  scope?: IAnyObject;
  store?: IStore<V, R> | IStoreConfig<V, R>;
}

const isPlainObject = (value: unknown): value is IAnyObject => (
  Boolean(value)
  && typeof value === 'object'
  && !Array.isArray(value)
  && Object.getPrototypeOf(value) === Object.prototype
);

const EMPTY_DYNAMIC_VIEW_CONFIG: DynamicViewConfig = {};

function mergeMaybePlainObject<T>(source?: T, override?: T): T | undefined {
  if (!override) return source;
  if (!source) return override;
  if (isPlainObject(source) && isPlainObject(override)) {
    const merged: IAnyObject = { ...source };
    Object.entries(override).forEach(([key, value]) => {
      const sourceValue = (source as IAnyObject)[key];
      merged[key] = isPlainObject(sourceValue) && isPlainObject(value)
        ? mergeMaybePlainObject(sourceValue, value)
        : value;
    });
    return merged as T;
  }
  return override;
}

const getEntitySchema = (config: DynamicViewConfig, type?: string) => (
  type ? config.schemas?.[type] ?? config.schema : config.schema
);

const getEntityStore = (config: DynamicViewConfig, type?: string) => (
  type ? config.stores?.[type] ?? config.store : config.store
);

const assertDynamicViewReady = (schema?: ISchema, store?: IStore | IStoreConfig, entity?: StoreEntity) => {
  if (!schema) {
    throw new Error('DynamicView 未找到 schema');
  }
  if (!store && !entity) {
    throw new Error('DynamicView 未找到 store 或 entity');
  }
};

export function DynamicView<
  V extends object = IAnyObject,
  R extends object = IAnyObject,
  B extends object = IAnyObject,
>(props: DynamicViewProps<V, R, B>) {
  const config = props.config ?? EMPTY_DYNAMIC_VIEW_CONFIG as DynamicViewConfig<V, R, B>;
  const resolvedType = props.type ?? config.type;

  const schema = useMemo(
    () => props.schema ?? getEntitySchema(config, resolvedType),
    [config, props.schema, resolvedType],
  );
  const store = useMemo(
    () => mergeMaybePlainObject(getEntityStore(config, resolvedType), props.store),
    [config, props.store, resolvedType],
  );
  const components = useMemo(
    () => mergeMaybePlainObject(config.components, props.components),
    [config.components, props.components],
  );
  const scope = useMemo(
    () => mergeMaybePlainObject(config.scope, props.scope),
    [config.scope, props.scope],
  );
  const entityProps = useMemo(
    () => mergeMaybePlainObject(config.entityProps, props.entityProps),
    [config.entityProps, props.entityProps],
  );
  const entity = props.entity ?? config.entity;
  const biz = props.biz ?? config.biz;
  const schemaKey = props.schemaKey ?? config.schemaKey;

  if (props.renderEntity) {
    return props.renderEntity({
      config,
      type: resolvedType,
    });
  }

  assertDynamicViewReady(schema, store, entity);

  const children = (
    <EntityAuto
      {...entityProps}
      key={props.resetKey}
      biz={biz}
      components={components}
      entity={entity}
      schema={schema}
      schemaKey={schemaKey}
      scope={scope}
      store={store}
      type={resolvedType}
    />
  );

  if (props.renderContainer) {
    return props.renderContainer({
      children,
      config,
      type: resolvedType,
    });
  }

  return children;
}
